import { Button, ConfirmModal, Input, notify, Switch } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { Upload } from '@affine/core/components/pure/file-upload';
import { EnableCloudPanel } from '@affine/core/desktop/dialogs/setting/workspace-setting/preference/enable-cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { UserFriendlyError } from '@affine/error';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import type React from 'react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { SettingsCollapsibleSection as CollapsibleSection } from '../../../desktop/dialogs/setting/workspace-setting/shared/settings-collapsible';
import { ServerService } from '../../cloud/services/server';
import { WorkspaceService } from '../../workspace/services/workspace';
import { COUNT_PER_PAGE } from '../constants';
import { EmbeddingService } from '../services/embedding';
import { EmbeddingStore } from '../stores/embedding';
import { Attachments } from './attachments';
import EmbeddingProgress from './embedding-progress';
import { IgnoredDocs } from './ignored-docs';

interface EmbeddingSettingsProps {
  /** When true, renders content without SettingWrapper (for embedding in another section) */
  bare?: boolean;
  advancedExtra?: ReactNode;
}

const EmbeddingLocal: React.FC<{}> = () => {
  return <EnableCloudPanel />;
};

const EmbeddingCloud: React.FC<{
  disabled: boolean;
  bare?: boolean;
  advancedExtra?: ReactNode;
}> = ({ disabled, bare, advancedExtra }) => {
  const t = useI18n();
  const embeddingService = useService(EmbeddingService);
  const embeddingStore = useService(EmbeddingStore);
  const workspaceService = useService(WorkspaceService);
  const workspaceDialogService = useService(WorkspaceDialogService);

  const [reindexConfirmOpen, setReindexConfirmOpen] = useState(false);
  const [reindexInputValue, setReindexInputValue] = useState('');
  const [isReindexing, setIsReindexing] = useState(false);

  const embeddingEnabled = useLiveData(
    embeddingService.embeddingEnabled.enabled$
  );
  const attachments = useLiveData(
    embeddingService.additionalAttachments.mergedAttachments$
  );
  const ignoredDocs = useLiveData(embeddingService.ignoredDocs.docs$);
  const embeddingProgress = useLiveData(
    embeddingService.embeddingProgress.progress$
  );
  const { totalCount } = useLiveData(
    embeddingService.additionalAttachments.attachments$
  );
  const isIgnoredDocsLoading = useLiveData(
    embeddingService.ignoredDocs.loading$
  );
  const isEnabledLoading = useLiveData(
    embeddingService.embeddingEnabled.loading$
  );

  const handleEmbeddingToggle = useCallback(
    (checked: boolean) => {
      track.$.settingsPanel.indexerEmbedding.toggleWorkspaceEmbedding({
        type: 'Embedding',
        control: 'Workspace embedding',
        option: checked ? 'on' : 'off',
      });

      embeddingService.embeddingEnabled
        .setEnabled(checked)
        .then(() => {
          if (checked) {
            embeddingService.embeddingProgress.startEmbeddingProgressPolling();
          }
        })
        .catch((error: unknown) => {
          const err = UserFriendlyError.fromAny(error);
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.indexer-embedding.embedding.switch.error'
              ](),
            message: t[`error.${err.name}`](err.data),
          });
        });
    },
    [embeddingService.embeddingEnabled, embeddingService.embeddingProgress, t]
  );

  const handleAttachmentUpload = useCallback(
    (file: File) => {
      track.$.settingsPanel.indexerEmbedding.addAdditionalDocs({
        type: 'Embedding',
        control: 'Select doc',
        docType: file.type,
      });
      embeddingService.additionalAttachments.addAttachments([file]);
      // Restart polling to track progress of newly uploaded files
      embeddingService.embeddingProgress.startEmbeddingProgressPolling();
    },
    [embeddingService.additionalAttachments, embeddingService.embeddingProgress]
  );

  const handleAttachmentsDelete = useCallback(
    (fileId: string) => {
      embeddingService.additionalAttachments
        .removeAttachment(fileId)
        .catch((error: unknown) => {
          const err = UserFriendlyError.fromAny(error);
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.indexer-embedding.embedding.remove-attachment.error'
              ](),
            message: t[`error.${err.name}`](err.data),
          });
        });
    },
    [embeddingService.additionalAttachments, t]
  );

  const handleAttachmentsPageChange = useCallback(
    (offset: number) => {
      embeddingService.additionalAttachments.getAttachments({
        offset,
      });
    },
    [embeddingService.additionalAttachments]
  );

  const handleReindex = useCallback(async () => {
    setIsReindexing(true);
    try {
      const result = await embeddingStore.reindexWorkspace(
        workspaceService.workspace.id
      );
      notify.success({
        title: 'Re-Index started',
        message: `${result.deletedChunks} chunks deleted, ${result.queuedDocs} docs queued for re-indexing.`,
      });
      // Restart progress polling to track re-indexing
      embeddingService.embeddingProgress.startEmbeddingProgressPolling();
    } catch (error) {
      const err = UserFriendlyError.fromAny(error);
      notify.error({
        title: 'Re-Index failed',
        message: err.message,
      });
    } finally {
      setIsReindexing(false);
      setReindexConfirmOpen(false);
    }
  }, [
    embeddingStore,
    workspaceService.workspace.id,
    embeddingService.embeddingProgress,
  ]);

  const handleSelectDoc = useCallback(() => {
    if (isIgnoredDocsLoading) {
      return;
    }
    const initialIds = ignoredDocs.map((doc: { docId: string }) => doc.docId);
    workspaceDialogService.open(
      'doc-selector',
      {
        init: initialIds,
      },
      (selectedIds: string[] | undefined) => {
        if (selectedIds === undefined) {
          return;
        }
        track.$.settingsPanel.indexerEmbedding.addIgnoredDocs({
          type: 'Embedding',
          control: 'Additional docs',
          result: 'success',
        });
        const add = selectedIds.filter(
          (id: string) => !initialIds?.includes(id)
        );
        const remove = initialIds?.filter(
          (id: string) => !selectedIds.includes(id)
        );
        embeddingService.ignoredDocs
          .updateIgnoredDocs({ add, remove })
          .catch((error: unknown) => {
            const err = UserFriendlyError.fromAny(error);
            notify.error({
              title:
                t[
                  'com.streamlain.settings.workspace.indexer-embedding.embedding.update-ignored-docs.error'
                ](),
              message: t[`error.${err.name}`](err.data),
            });
          });
      }
    );
  }, [
    ignoredDocs,
    isIgnoredDocsLoading,
    workspaceDialogService,
    embeddingService.ignoredDocs,
    t,
  ]);

  useEffect(() => {
    embeddingService.embeddingProgress.startEmbeddingProgressPolling();
    embeddingService.embeddingEnabled.getEnabled();
    embeddingService.additionalAttachments.getAttachments({
      first: COUNT_PER_PAGE,
      after: null,
    });
    embeddingService.ignoredDocs.getIgnoredDocs();
    embeddingService.embeddingProgress.getEmbeddingProgress();

    return () => {
      embeddingService.embeddingProgress.stopEmbeddingProgressPolling();
    };
  }, [
    embeddingService.embeddingProgress,
    embeddingService.embeddingEnabled,
    embeddingService.additionalAttachments,
    embeddingService.ignoredDocs,
  ]);

  const content = (
    <>
      <SettingRow
        name={t[
          'com.streamlain.settings.workspace.indexer-embedding.embedding.switch.title'
        ]()}
        desc={t[
          'com.streamlain.settings.workspace.indexer-embedding.embedding.switch.description'
        ]()}
        style={{ marginBottom: 10 }}
      >
        <Switch
          data-testid="workspace-embedding-setting-switch"
          checked={embeddingEnabled ?? false}
          onChange={handleEmbeddingToggle}
          disabled={isEnabledLoading}
        />
      </SettingRow>

      {/* Advanced section — tight spacing to Intelligent Search toggle */}
      <div style={{ marginBottom: '16px' }}>
        <CollapsibleSection
          title={
            t[
              'com.streamlain.settings.workspace.indexer-embedding.advanced'
            ]?.() ?? 'Advanced'
          }
          testId="embedding-advanced"
        >
          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.progress.title'
            ]()}
            style={{ marginBottom: '0px' }}
          />
          <EmbeddingProgress
            status={embeddingProgress}
            workspaceId={workspaceService.workspace.id}
          />

          <SettingRow
            name="Re-Index"
            desc="Delete all existing embeddings and re-index the entire workspace."
          >
            <Button
              data-testid="workspace-embedding-reindex-button"
              variant="error"
              onClick={() => setReindexConfirmOpen(true)}
              disabled={disabled || isReindexing}
            >
              {isReindexing ? 'Indexing...' : 'Re-Index'}
            </Button>
          </SettingRow>

          <ConfirmModal
            open={reindexConfirmOpen}
            onOpenChange={v => {
              setReindexConfirmOpen(v);
              if (!v) setReindexInputValue('');
            }}
            title="Workspace Re-Index"
            description="All existing embeddings will be permanently deleted and the entire workspace will be re-indexed. This may take several minutes depending on workspace size."
            confirmText={isReindexing ? 'Indexing...' : 'Re-Index'}
            confirmButtonOptions={{
              variant: 'error',
              disabled: reindexInputValue.trim() !== 'RE-INDEX' || isReindexing,
              loading: isReindexing,
            }}
            onConfirm={handleReindex}
            cancelText="Cancel"
          >
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              Type{' '}
              <strong
                style={{ color: 'var(--streamlain-error-color, #ef4444)' }}
              >
                RE-INDEX
              </strong>{' '}
              to confirm:
            </div>
            <Input
              type="text"
              value={reindexInputValue}
              onChange={setReindexInputValue}
              placeholder="RE-INDEX"
              data-testid="workspace-embedding-reindex-confirm-input"
            />
          </ConfirmModal>

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.additional-attachments.title'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.additional-attachments.description'
            ]()}
          >
            <Upload fileChange={handleAttachmentUpload}>
              <Button
                data-testid="workspace-embedding-setting-upload-button"
                variant="primary"
              >
                {t[
                  'com.streamlain.settings.workspace.indexer-embedding.embedding.upload-file'
                ]()}
              </Button>
            </Upload>
          </SettingRow>

          {attachments.length > 0 && (
            <Attachments
              disabled={disabled}
              attachments={attachments}
              onDelete={handleAttachmentsDelete}
              totalCount={totalCount}
              onPageChange={handleAttachmentsPageChange}
            />
          )}

          <SettingRow
            name={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.ignore-docs.title'
            ]()}
            desc={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.ignore-docs.description'
            ]()}
          >
            <Button
              data-testid="workspace-embedding-setting-ignore-docs-button"
              variant="primary"
              onClick={handleSelectDoc}
            >
              {t[
                'com.streamlain.settings.workspace.indexer-embedding.embedding.select-doc'
              ]()}
            </Button>
          </SettingRow>

          {ignoredDocs.length > 0 && (
            <IgnoredDocs
              ignoredDocs={ignoredDocs}
              isLoading={isIgnoredDocsLoading}
            />
          )}

          {advancedExtra}
        </CollapsibleSection>
      </div>
    </>
  );

  if (bare) return content;

  return (
    <SettingWrapper
      title={
        t[
          'com.streamlain.settings.workspace.indexer-embedding.embedding.title'
        ]?.() ?? 'Document Analysis'
      }
      testId="workspace-embedding-setting-wrapper"
      disabled={disabled}
    >
      {content}
    </SettingWrapper>
  );
};

export const EmbeddingSettings: React.FC<EmbeddingSettingsProps> = ({
  bare,
  advancedExtra,
}) => {
  const workspaceService = useService(WorkspaceService);
  const serverService = useService(ServerService);
  const isLocal = workspaceService.workspace.flavour === 'local';
  const serverConfig = useLiveData(serverService.server.config$);
  const isEmbeddingEnabled = serverConfig?.features.includes(
    ServerFeature.CopilotEmbedding
  );

  // sl-qc6h gating: Hide entirely when embedding is not enabled by platform admin
  if (!isEmbeddingEnabled) {
    return null;
  }

  return isLocal ? (
    <EmbeddingLocal />
  ) : (
    <EmbeddingCloud
      disabled={false}
      bare={bare}
      advancedExtra={advancedExtra}
    />
  );
};
